// src/components/Notifications.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { onMessage } from '../firebase';
import './Notifications.css';

function Notifications({ userId }) {
  const [notificationIds, setNotificationIds] = useState(new Set());
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountTimestampRef = useRef(new Date().toISOString());
  const notificationTimeouts = useRef(new Map());

  const groupNotifications = (notifs) => {
    const grouped = {};
    notifs.forEach((notif) => {
      if (notif.is_read) return;
      if (new Date(notif.timestamp) < new Date(mountTimestampRef.current)) {
        console.log('Skipping old notification:', notif);
        return;
      }
      const key = notif.notification_type === 'chat_message' ? `chat_${notif.sender_id}_${notif.id}` : `system_${notif.id}`;
      if (!grouped[key]) {
        grouped[key] = {
          id: notif.id || `temp-${Date.now()}`,
          sender_id: notif.sender_id || null,
          sender_username: notif.sender_username || 'System',
          notification_type: notif.notification_type || 'system',
          count: 0,
          latestMessage: notif.message,
          timestamp: notif.timestamp,
          link: notif.link || '/',
          unread: !notif.is_read,
        };
      }
      grouped[key].count += 1;
      if (new Date(notif.timestamp) > new Date(grouped[key].timestamp)) {
        grouped[key].timestamp = notif.timestamp;
        grouped[key].latestMessage = notif.message;
        grouped[key].link = notif.link;
      }
    });
    return Object.values(grouped).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const markNotificationRead = async (id) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/mark_notification_read/`,
        { notification_id: id },
        { withCredentials: true }
      );
      setNotificationIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (error) {
    }
  };

  const handleNotificationClick = async (link, id) => {
    if (!link) {
      return;
    }
    clearTimeout(notificationTimeouts.current.get(id));
    notificationTimeouts.current.delete(id);
    await markNotificationRead(id);
    toast.dismiss(id);
    // Parse link to preserve query and hash
    const url = new URL(link);
    const path = url.pathname + url.search + url.hash;
    console.log('Navigating to:', path);
    navigate(path);
  };

  const handleDismiss = async (id) => {
    clearTimeout(notificationTimeouts.current.get(id));
    notificationTimeouts.current.delete(id);
    await markNotificationRead(id);
    toast.dismiss(id);
    setNotificationIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const displayNotification = (notif) => {
    if (notificationIds.has(notif.id)) {
      console.log('Duplicate notification ignored:', notif.id);
      return;
    }
    setNotificationIds((prev) => new Set(prev).add(notif.id));
    const message =
      notif.notification_type === 'chat_message' && notif.count > 1
        ? `You have ${notif.count} new messages from ${notif.sender_username}`
        : notif.notification_type === 'chat_message'
        ? `New message from ${notif.sender_username}: ${notif.latestMessage}`
        : notif.latestMessage;

    toast(
      (t) => (
        <div
          className="notification-content"
          onClick={() => handleNotificationClick(notif.link, notif.id)}
        >
          <p className="notification-message">{message}</p>
          <span className="notification-time">
            {new Date(notif.timestamp).toLocaleTimeString()}
          </span>
          <button
            className="notification-close"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss(notif.id);
            }}
          >
            ×
          </button>
        </div>
      ),
      {
        id: notif.id,
        duration: notif.notification_type === 'warning' ? 10000 : 5000,
        position: 'top-right',
        className: `notification ${notif.unread ? 'unread' : ''} ${
          notif.notification_type === 'warning' ? 'warning' : ''
        }`,
      }
    );
    const timeoutId = setTimeout(() => {
      markNotificationRead(notif.id);
      notificationTimeouts.current.delete(notif.id);
    }, notif.notification_type === 'warning' ? 10000 : 5000);
    notificationTimeouts.current.set(notif.id, timeoutId);
  };

  const fetchNotifications = async () => {
    if (!userId) {
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/notifications/?since=${mountTimestampRef.current}`,
        { withCredentials: true }
      );
      const notifications = response.data.notifications || [];

      const groupedNotifs = groupNotifications(
        notifications.map((notif) => ({
          ...notif,
          sender_id: notif.sender_id || null,
          sender_username:
            notif.sender_id && notif.sender_id !== userId
              ? notif.sender_username || 'Unknown'
              : 'System',
        }))
      );
      groupedNotifs.forEach(displayNotification);
    } catch (error) {
      if (error.response) {
        console.error('Response details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        });
      }
    }
  };

  const connectWebSocket = () => {
    if (!userId) {
      console.log('No userId, skipping WebSocket setup');
      return;
    }

    const wsUrl = `${process.env.REACT_APP_WS_URL}/ws/notifications/${userId}/`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      clearTimeout(reconnectTimeoutRef.current);
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'notification') {
          displayNotification({
            id: data.chat_message_id || `ws-${Date.now()}`,
            message: data.message,
            link: data.link,
            timestamp: data.timestamp,
            notification_type: data.notification_type,
            sender_id: data.sender_id || null,
            sender_username: data.sender_username || 'System',
            unread: data.unread,
            count: 1,
          });
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    wsRef.current.onclose = (event) => {
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 5000);
    };

    wsRef.current.onerror = (error) => {
      wsRef.current.close();
    };
  };

  useEffect(() => {
    if (userId) {
      connectWebSocket();
      fetchNotifications();
      const intervalId = setInterval(fetchNotifications, 10000);
      return () => {
        console.log('Closing WebSocket');
        clearTimeout(reconnectTimeoutRef.current);
        clearInterval(intervalId);
        wsRef.current?.close();
      };
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const unsubscribe = onMessage(
      (payload) => {
      const id = payload.data?.id || payload.messageId || `fcm-${Date.now()}`;
        if (notificationIds.has(id)) {
          console.log('Duplicate FCM notification ID: ignored:', id);
          return;
        }
        const timestamp = payload.data?.timestamp || new Date().toISOString();
        if (new Date(timestamp) < new Date(mountTimestampRef.current)) {
          console.log('Skipping old FCM notification:', timestamp);
          return;
        }
        displayNotification({
          id,
          message: payload.notification?.body || 'You have a new notification',
          link: payload.data?.link || '/',
          timestamp,
          notification_type: payload.data?.type || 'system',
          sender_id: payload.data?.sender_id || null,
          sender_username: payload.data?.sender_username || null,
          unread: true,
          count: 1,
        });
      },
      (error) => {
        console.error('FCM onMessage error:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  return null;
}

export default Notifications;
